//@flow
import React, {useState} from 'react';
import {
  makeStyles,
  TextField,
  IconButton,
  Collapse,
  Typography,
  Button,
  Chip,
} from '@material-ui/core';
import {
  ExpandMore,
  ExpandLess,
  Delete,
  AttachFile,
  Close,
  GetApp,
  InsertDriveFile,
  Image as ImageIcon,
  Videocam,
  Audiotrack,
} from '@material-ui/icons';
import {colors} from '../../../../assets/styles/colors';
import {getFileUrl} from '../../../../utils/image_provider';
import type {TrainedMessageAttachment} from '../../../../entities';

const DOCUMENT_FILE_EXTENSIONS = '.pdf,.xlsx,.xls';
const IMAGE_FILE_EXTENSIONS = '.jpg,.jpeg,.png,.gif,.webp,.avif';

/*
const VIDEO_FILE_EXTENSIONS = '.mp4,.webm,.mov,.avi,.mkv';
const AUDIO_FILE_EXTENSIONS = '.mp3,.wav,.ogg,.m4a,.aac';
 */
const ACCEPTED_FILE_EXTENSIONS = `${DOCUMENT_FILE_EXTENSIONS},${IMAGE_FILE_EXTENSIONS}`;

const useStyles = makeStyles(theme => ({
  itemContainer: {
    border: `1px solid ${colors.grey200}`,
    borderRadius: '12px',
    overflow: 'hidden',
    flexShrink: 0,
    backgroundColor: 'white',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    height: 'min-content',
    '&:hover': {
      borderColor: colors.grey300,
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    },
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
    padding: '.5rem .75rem',
    backgroundColor: colors.grey50,
    borderBottom: `1px solid ${colors.grey200}`,
  },
  questionInput: {
    flex: 1,
    '& .MuiInputBase-input': {
      fontSize: '0.95rem',
      fontWeight: 500,
      color: colors.grey900,
    },
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  actionButton: {
    padding: '0.5rem',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  deleteButton: {
    color: colors.danger,
    '&:hover': {
      backgroundColor: 'rgba(220, 53, 69, 0.08)',
    },
  },
  content: {
    padding: '.75rem',
  },
  answerTextarea: {
    width: '100%',
    '& .MuiInputBase-root': {
      fontSize: '0.9rem',
      lineHeight: 1.6,
      padding: '.5rem 0.75rem',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: colors.grey200,
      },
      '&:hover fieldset': {
        borderColor: colors.grey300,
      },
      '&.Mui-focused fieldset': {
        borderColor: colors.primary,
        borderWidth: '2px',
      },
    },
  },
  fileSection: {
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: colors.grey50,
    borderRadius: '8px',
    border: `1px dashed ${colors.grey300}`,
  },
  fileUploadButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.85rem',
    color: colors.primary,
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'color 0.2s ease',
    '&:hover': {
      color: colors.primaryDark,
      textDecoration: 'underline',
    },
  },
  attachmentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginBottom: '0.75rem',
  },
  attachmentItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0.5rem 0.75rem',
    backgroundColor: 'white',
    borderRadius: '6px',
    border: `1px solid ${colors.grey300}`,
  },
  attachmentInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    flex: 1,
    overflow: 'hidden',
  },
  attachmentIcon: {
    color: colors.grey600,
    fontSize: '1.2rem',
  },
  attachmentName: {
    fontSize: '0.85rem',
    color: colors.grey700,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontWeight: 500,
  },
  attachmentActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  attachmentChip: {
    fontSize: '0.7rem',
    height: '20px',
  },
  pendingChip: {
    backgroundColor: colors.warning,
    color: 'white',
  },
  errorChip: {
    backgroundColor: colors.danger,
    color: 'white',
  },
  fileError: {
    fontSize: '0.8rem',
    color: colors.danger,
    marginTop: '0.5rem',
    fontWeight: 500,
  },
}));

type PendingAttachment = {
  file: File,
  error?: string,
};

type Props = {
  question: string,
  answer: string,
  attachments?: TrainedMessageAttachment[],
  pendingAttachments?: PendingAttachment[],
  maxAttachments?: number,
  onQuestionChange: (index: number, value: string) => void,
  onAnswerChange: (index: number, value: string) => void,
  onFileChange: (index: number, file: File | null) => void,
  onAttachmentRemove?: (index: number, attachmentIndex: number) => void,
  onPendingAttachmentRemove?: (index: number, pendingIndex: number) => void,
  onDelete: (index: number) => void,
  index: number,
};

const getAttachmentIcon = (type: string) => {
  switch (type) {
    case 'image':
      return <ImageIcon />;
    case 'video':
      return <Videocam />;
    case 'audio':
      return <Audiotrack />;
    default:
      return <InsertDriveFile />;
  }
};

const FAQItem = React.memo(function FAQItem(props: Props) {
  const {
    index,
    question,
    answer,
    attachments = [],
    pendingAttachments = [],
    maxAttachments = 3,
    onQuestionChange,
    onAnswerChange,
    onFileChange,
    onAttachmentRemove,
    onPendingAttachmentRemove,
    onDelete,
  } = props;

  const totalAttachments = attachments.length + pendingAttachments.length;
  const canAddMoreAttachments = totalAttachments < maxAttachments;
  const classes = useStyles();
  const [expanded, setExpanded] = useState(true);

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const handleFileSelect = (event: SyntheticInputEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      onFileChange(index, selectedFile);
    }

    event.target.value = '';
  };

  const handleRemoveAttachment = (attachmentIndex: number) => {
    if (onAttachmentRemove) {
      onAttachmentRemove(index, attachmentIndex);
    }
  };

  const handleRemovePendingAttachment = (pendingIndex: number) => {
    if (onPendingAttachmentRemove) {
      onPendingAttachmentRemove(index, pendingIndex);
    }
  };

  const handleDownloadAttachment = (attachment: TrainedMessageAttachment) => {
    const url = attachment.url || getFileUrl(attachment.file_id);
    window.open(url, '_blank');
  };

  return (
    <div className={classes.itemContainer}>
      <div className={classes.titleContainer}>
        <TextField
          className={classes.questionInput}
          value={question}
          onChange={e => onQuestionChange(index, e.target.value)}
          placeholder="Escribe la pregunta..."
          variant="standard"
          InputProps={{
            disableUnderline: true,
          }}
        />
        <div className={classes.actions}>
          <IconButton
            size="small"
            onClick={handleToggle}
            className={classes.actionButton}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete(index)}
            className={`${classes.actionButton} ${classes.deleteButton}`}
          >
            <Delete />
          </IconButton>
        </div>
      </div>
      <Collapse in={expanded}>
        <div className={classes.content}>
          <TextField
            multiline
            rows={4}
            variant="outlined"
            placeholder="Escribe la respuesta..."
            value={answer}
            onChange={e => onAnswerChange(index, e.target.value)}
            className={classes.answerTextarea}
            fullWidth
          />
          <div className={classes.fileSection}>
            {/* Existing attachments */}
            {attachments.length > 0 && (
              <div className={classes.attachmentsList}>
                {attachments.map((attachment, attachmentIndex) => (
                  <div
                    key={attachment.file_id}
                    className={classes.attachmentItem}
                  >
                    <div className={classes.attachmentInfo}>
                      <span className={classes.attachmentIcon}>
                        {getAttachmentIcon(attachment.type)}
                      </span>
                      <Typography className={classes.attachmentName}>
                        {attachment.name || attachment.file_id}
                      </Typography>
                    </div>
                    <div className={classes.attachmentActions}>
                      <Button
                        size="small"
                        startIcon={<GetApp />}
                        onClick={() => handleDownloadAttachment(attachment)}
                        style={{textTransform: 'none'}}
                      >
                        Descargar
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveAttachment(attachmentIndex)}
                      >
                        <Close style={{fontSize: '1rem'}} />
                      </IconButton>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pending attachments (not yet uploaded) */}
            {pendingAttachments.length > 0 && (
              <div className={classes.attachmentsList}>
                {pendingAttachments.map((pending, pendingIndex) => (
                  <div key={pendingIndex} className={classes.attachmentItem}>
                    <div className={classes.attachmentInfo}>
                      <span className={classes.attachmentIcon}>
                        <AttachFile />
                      </span>
                      <Typography className={classes.attachmentName}>
                        {pending.file.name}
                      </Typography>
                      <Chip
                        label={pending.error ? 'Error' : 'Pendiente'}
                        size="small"
                        className={`${classes.attachmentChip} ${
                          pending.error
                            ? classes.errorChip
                            : classes.pendingChip
                        }`}
                      />
                    </div>
                    <div className={classes.attachmentActions}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleRemovePendingAttachment(pendingIndex)
                        }
                      >
                        <Close style={{fontSize: '1rem'}} />
                      </IconButton>
                    </div>
                    {pending.error && (
                      <Typography className={classes.fileError}>
                        {pending.error}
                      </Typography>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Add new attachment button */}
            {canAddMoreAttachments && (
              <>
                <input
                  type="file"
                  id={`file-upload-${index}`}
                  hidden
                  accept={ACCEPTED_FILE_EXTENSIONS}
                  onChange={handleFileSelect}
                />
                <label
                  htmlFor={`file-upload-${index}`}
                  className={classes.fileUploadButton}
                >
                  <AttachFile style={{fontSize: '1.1rem'}} />
                  <span>Adjuntar archivo (PDF, Excel, imagen)</span>
                </label>
              </>
            )}
          </div>
        </div>
      </Collapse>
    </div>
  );
});

export default FAQItem;
